import React, { useState, useCallback, ReactElement, useEffect } from 'react';
// import { Dimensions } from 'react-native'
import { IconButton, Dialog } from '@framework-controls/core';
import { IconMore, IconRemove } from 'src/content/icons';
import css from './MoreDetails.module.scss';
import { strings } from 'src/localization';
import SVPButton from '../SVPButtons/SVPButton';
import classNames from 'classnames';
import { ascCtaInteractionInput, ascPageviewInput } from 'src/models/GA4Events/GA4Events';
import { ascCtaInteraction, ascPageview } from 'src/helpers/EventHelper/EventHelper';
import { ApptVeh, SpecData, VehicleRec } from 'src/models';
import { getProviderId, sendSdEvent } from 'src/helpers/ShiftDigitalHelper/ShiftDigitalHelper';

interface IProps {
    headerLit: string;
    message?: string;
    children: React.ReactNode;
    addServiceBtn?: boolean;
    recallDialog?: boolean;
    // Allow overriding of the open flag
    openFlag?: boolean;
    vehicles: VehicleRec[]
    selectedVehicle?: string;
    theme: string;
    specData?: SpecData;
    sessionId: string;
    lexusMCLaunched: string;
    toyotaMCLaunched: string;
    lexusTDMCLaunched: string;
    toyotaTDMCLaunched: string;
    apptVeh: ApptVeh;
    setOpenFlag?: (open: boolean) => void;
    onAddService?: (e: any, btnText: string) => (void);
}

const MoreDetails = (props: IProps) => {
    const [openFlag, setOpenFlag] = useState(false);

    // button message is set to props.
    // if button message is blank then set to MoreDetails
    let btnMessage = props.message;
    let menusActive = false;
    const isSDAnalyticsEnabled =
        props.specData?.toyotaTrackingFlag === 'Y' ||
        props.specData?.lexusTrackingFlag === 'Y' ||
        props.specData?.mbzTrackingFlag === 'Y' ||
        props.specData?.mazdaCATrackingFlag === 'Y' ||
        props.specData?.vwTrackingFlag === 'Y';
    const paperClass = classNames(
        css.paper,
        {
            [css.paperMC]: btnMessage === 'MAINTCONTRACT',
        },
    );
    if (btnMessage === 'MENUS' || btnMessage === 'MAINTCONTRACT') {
        menusActive = true;
    }
    if (!btnMessage || menusActive) {
        btnMessage = strings.MoreDetails;
    }

    const headerBoxStyles = classNames(
        css.headerbox,
        {
            [css.headerboxMazda]: props.theme === 'mazda',
        },
    );

    // if dialogHandler then invert the OpenFlag
    const dialogHandler = useCallback((e: any) => {
        let eventAction = 'open';
        if (props.setOpenFlag && props.openFlag !== undefined) {
            props.setOpenFlag(!props.openFlag);
            if (props.openFlag) {
                eventAction = 'close';
            }
        } else {
            setOpenFlag(!openFlag);
            if (openFlag) {
                eventAction = 'close';
            }
        }
        // triggers twice on close if we click the backdrop.
        const ascCtaInteractionInp : ascCtaInteractionInput = {
            pageType: 'service',
            elementText: strings.MoreDetails,
            elementColor: '',
            elementOrder: '',
            elementType: 'item_details',
            elementSubType: 'information_link',
            eventAction: 'click',
            eventActionResult: eventAction,
            linkUrl: '',
        };
        ascCtaInteraction(ascCtaInteractionInp, e, props.vehicles, props.selectedVehicle);
    }, [openFlag, props.openFlag, props.setOpenFlag, props.vehicles, props.selectedVehicle]);

    // Add Service Handler, close Dialog modal
    // if we have an onAddService function, proform it
    const addSvcHandler = (e: any, btnText: string) => {
        if (props.setOpenFlag && props.openFlag !== undefined) {
            props.setOpenFlag(false);
        } else {
            setOpenFlag(false);
        }
        if (props.onAddService) {
            props.onAddService!(e, btnText);
            if (isSDAnalyticsEnabled) {
                sendSdEvent('scheduleServiceSelectionStart', {
                    scheduleServiceSessionId: props.sessionId,
                    scheduleServiceProvider: getProviderId(props.specData, props.toyotaMCLaunched,
                        props.toyotaTDMCLaunched, props.lexusMCLaunched, props.lexusTDMCLaunched),
                    scheduleServiceVehicleMake: props.apptVeh.make,
                    scheduleServiceVehicleMileage: props.apptVeh.mileage,
                    scheduleServiceVehicleModel: props.apptVeh.model,
                    scheduleServiceVehicleVin: props.apptVeh.vin,
                    scheduleServiceVehicleYear: props.apptVeh.vehYr,
                });
            }
        }
    };

    const dialogButtonArea = (
        <div className={css.footer}>
            <hr className={css.horizLine}/>
            <div className={css.btnArea}>
                {
                    props.addServiceBtn === true ?
                        <SVPButton
                            id='addSvc'
                            handler={(e) => addSvcHandler(e, strings.AddIndSvcBtnText)}
                            message={strings.AddIndSvcBtnText}
                            noArrow
                            variant={'secondaryBtn'}
                            theme={props.theme}
                        /> :
                        <SVPButton
                            id='removeSvc'
                            handler={(e) => addSvcHandler(e, strings.RemoveSvcBtnText)}
                            message={strings.RemoveSvcBtnText}
                            variant={'secondaryBtn'}
                            noArrow
                            theme={props.theme}/>
                }
            </div>
        </div>
    );

    useEffect(() => {
        if (props.openFlag || (props.openFlag === undefined && openFlag)) {
            const ascPageViewInp: ascPageviewInput = {
                pageType: 'service',
                errorCode: '',
                itemResults: '',
            };
            ascPageview(ascPageViewInp, props.vehicles, props.selectedVehicle);
        }
    }, [props.selectedVehicle, props.vehicles, props.openFlag, openFlag]);

    let dialog: ReactElement | null = null;
    if (menusActive || props.recallDialog) {
        dialog = <Dialog
            cssClasses={{
                content: props.recallDialog ? css.contentArea : css.content,
                paper: paperClass,
                header: css.LightBoxHeader,
            }}
            // Override the openFlag with the props openFlag if defined
            isOpen={props.openFlag !== undefined ? props.openFlag : openFlag}
            anchorOrigin={{
                vertical: 'top',
            }}
            header={<div>{props.headerLit}</div>}
            hideSave={true}
            hideCancel={true}
            disableUpperCase={true}
            onCancel={dialogHandler}
            onBackDropClick={dialogHandler}
            width={'full'}
        >
            <div className={headerBoxStyles}>
                {props.headerLit}
                <div className={css.headerIcon}>
                    <IconRemove id='closeBtn' onClick={dialogHandler}/>
                </div>
            </div>
            {props.children}
        </Dialog>;
    } else {
        dialog = <Dialog
            cssClasses={{
                content: css.content,
                paper: paperClass,
                header: css.LightBoxHeader,
            }}
            // Override the openFlag with the props openFlag if defined
            isOpen={props.openFlag !== undefined ? props.openFlag : openFlag}
            anchorOrigin={{
                vertical: 'top',
            }}
            header={<div>{props.headerLit}</div>}
            hideSave={true}
            hideCancel={true}
            disableUpperCase={true}
            onCancel={dialogHandler}
            onBackDropClick={dialogHandler}
            width={'full'}
            // customButtons={customButtons}
            onCustomClicked={addSvcHandler}
        >
            <div className={headerBoxStyles}>
                {props.headerLit}
                <div className={css.headerIcon}>
                    <IconRemove id='closeBtn' onClick={dialogHandler}/>
                </div>
            </div>
            {props.children}
            {dialogButtonArea}
        </Dialog>;
    }

    const smallStyles = classNames(
        css.small,
        {
            [css.smallFord]: props.theme === 'ford',
        },
    );

    const smallSecondaryStyles = classNames(
        css.smallSecondary,
        {
            [css.smallCV]: props.theme === 'chevrolet',
            [css.smallBMW]: props.theme === 'bmw',
            [css.smallHonda]: props.theme === 'honda',
            [css.smallFord]: props.theme === 'ford',
            [css.smallMB]: props.theme === 'mercedesBenz',
            [css.smallMazda]: props.theme === 'mazda',
        },
    );

    let noDots = false;
    if (props.theme === 'toyotaSPM' || props.theme === 'lexusMonogram' || props.theme === 'chevrolet' ||
        props.theme === 'honda' || props.theme === 'buick' || props.theme === 'bmw' ||
        props.theme === 'infiniti' || props.theme === 'nissan' || props.theme === 'mercedesBenz' || props.theme === 'mazda' ||
        props.theme === 'acura') {
        noDots = true;
    }

    return (
        <div>
            {noDots ? (
                <IconButton
                    id={props.headerLit + 'btn'}
                    onClick={dialogHandler}
                    variant='moreAlt'
                >
                    <div className={css.literal}>
                        <small className={smallSecondaryStyles}>
                            {btnMessage}
                        </small>
                    </div>
                </IconButton>
            ) : (
                <IconButton
                    id={props.headerLit + 'btn'}
                    onClick={dialogHandler}
                    variant='more'
                >
                    <div className={css.literal} title={strings.ClickHereMoreDtl}>
                        <IconMore />
                        <small className={smallStyles}>{btnMessage}</small>
                    </div>
                </IconButton>
            )}
            {dialog}
        </div>
    );
};

export default MoreDetails;
